/**
 * Page-resume detection — ONE tracker for "the user came back to this tab".
 *
 * A phone lock, an app switch, or a long background tab freezes the page:
 * timers stop, and the OS tears down every socket WITHOUT the browser ever
 * delivering a `close` — a WebSocket still reports `OPEN`, a streaming fetch's
 * pending `read()` never rejects. Nothing in the page can tell on its own that
 * the world moved on. Returning to the foreground is the signal, and every
 * live channel the IDE holds (file-watch / presence / LSP / terminal sockets,
 * the sandbox boot stream, the durable sandbox status) needs the SAME signal
 * with the SAME "how long was I gone" measurement — so it lives here once,
 * instead of four hand-rolled `visibilitychange` listeners that each trusted
 * `readyState === OPEN` and therefore never revived a dead socket.
 *
 * Three browser events count as a resume:
 *   - `visibilitychange` → visible (the common phone-unlock / tab-switch case)
 *   - `pageshow` with `persisted` (a bfcache restore — the page's JS was frozen
 *     for the whole absence; a plain `pageshow` on a normal load is NOT a resume)
 *   - `online` (the network coming back is itself the signal, whatever the
 *     hidden duration — it skips the `minHiddenMs` gate)
 */

export type PageResumeReason = 'visible' | 'pageshow' | 'online'

export interface PageResumeInfo {
  /** Which browser event produced this resume. */
  reason: PageResumeReason
  /**
   * How long the page was hidden before this resume, in ms. `0` when it was
   * never observed hidden (e.g. an `online` event on a visible tab);
   * `Infinity` for a bfcache restore whose hide was never observed — treat it
   * as "gone for a long time" (it satisfies every threshold).
   */
  hiddenForMs: number
}

export type PageResumeListener = (info: PageResumeInfo) => void

/**
 * Sockets the server REAPS while the page is frozen: the API pings every 30s
 * and terminates a client that misses one pong, so past ~two intervals a
 * frozen page's socket is guaranteed gone server-side — the browser copy is a
 * zombie no matter what `readyState` says. Bounce it on resume.
 */
export const WS_SERVER_REAPED_AFTER_MS = 60_000

/**
 * Stateless subscription sockets (file-watch, presence) are cheap to bounce and
 * a mobile radio can drop them within seconds of a lock, so they bounce after
 * a much shorter absence. Ordinary quick tab switches stay below this.
 */
export const WS_BOUNCE_AFTER_HIDDEN_MS = 15_000

/**
 * Minimum absence before the workspace re-reads durable state (sandbox status,
 * WS ticket freshness) on resume. Filters ordinary quick tab switches so an
 * up-to-date IDE does not refetch on every one.
 */
export const RESUME_RECONCILE_AFTER_HIDDEN_MS = 5_000

const listeners = new Set<{ listener: PageResumeListener; minHiddenMs: number }>()
let hiddenAt: number | null = null
let installed = false

const dispatch = (reason: PageResumeReason, hiddenForMs: number): void => {
  for (const entry of listeners) {
    // `online` is its own signal — the network came back, whatever the tab did.
    if (reason !== 'online' && hiddenForMs < entry.minHiddenMs) continue
    try {
      entry.listener({ reason, hiddenForMs })
    } catch (error) {
      console.warn('[pageResume] listener failed', error)
    }
  }
}

const onVisibilityChange = (): void => {
  if (document.visibilityState === 'hidden') {
    if (hiddenAt === null) hiddenAt = Date.now()
    return
  }
  const hiddenForMs = hiddenAt === null ? 0 : Date.now() - hiddenAt
  hiddenAt = null
  dispatch('visible', hiddenForMs)
}

const onPageShow = (event: Event): void => {
  // A normal load fires `pageshow` too — only a bfcache restore is a resume.
  if (!(event as PageTransitionEvent).persisted) return
  const hiddenForMs = hiddenAt === null ? Number.POSITIVE_INFINITY : Date.now() - hiddenAt
  hiddenAt = null
  if (document.visibilityState === 'hidden') return
  dispatch('pageshow', hiddenForMs)
}

const onOnline = (): void => {
  if (document.visibilityState === 'hidden') return
  dispatch('online', hiddenAt === null ? 0 : Date.now() - hiddenAt)
}

const install = (): void => {
  if (installed || typeof document === 'undefined' || typeof window === 'undefined') return
  installed = true
  if (document.visibilityState === 'hidden') hiddenAt = Date.now()
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('pageshow', onPageShow)
  window.addEventListener('online', onOnline)
}

/**
 * Subscribe to page resumes.
 *
 * @param listener - Called on every resume that passes the gate.
 * @param minHiddenMs - Ignore resumes after a shorter absence (except `online`,
 *   which always fires). Defaults to 0 (every resume).
 * @returns An unsubscribe function.
 */
export const onPageResume = (listener: PageResumeListener, minHiddenMs = 0): (() => void) => {
  install()
  const entry = { listener, minHiddenMs }
  listeners.add(entry)
  return () => {
    listeners.delete(entry)
  }
}

/**
 * How long the page has currently been hidden (ms), or 0 while visible.
 * Lets a caller that only polls (no subscription) measure a gap on its own.
 *
 * @returns The current hidden duration in ms.
 */
export const currentHiddenForMs = (): number => {
  install()
  return hiddenAt === null ? 0 : Date.now() - hiddenAt
}

/**
 * Test-only: drop every listener and forget the hidden stamp. The DOM listeners
 * stay installed (they are idempotent and inert with no subscribers).
 */
export const resetPageResumeForTests = (): void => {
  listeners.clear()
  hiddenAt = null
}
